import { Component, OnInit } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { User } from '@microsoft/microsoft-graph-types';
import { GraphService } from '@services/graph.service';
import { NavigationService } from '@services/navigation.service';
import { LogoutReasonCode } from '@shared/_constants';
import { RouterLink } from '@shared/_constants';
import { DialogService } from '@services/dialog.service';
import { DeactivationStatus } from '@services/active-component.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { selectTPBMVersionAppSetting } from 'src/app/app.selectors';
import { selectIsGenerativeAiEnabledAppSetting } from 'src/app/app.selectors';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.css'],
})
export class NavMenuComponent implements OnInit {
  private destroy$ = new Subject();
  public isGenerativeAiEnabled = false;
  public sideExpanded = true;
  public topExpanded = false;
  public loggedIn = false;
  public currentUser: User;
  public uiBuildVersion: string;
  public apiBuildVersion$: Observable<string>;
  public tpbmVersion: string;
  public linkMyStudies = RouterLink.MyStudies;
  public linkSearchStudies = RouterLink.SearchStudies;
  public linkDashboard = RouterLink.Dashboard;

  private SIDEXPANDED_STORAGE_KEY = 'ey-page-sideExpanded';
  ngOnDestroy(): void {
    this.destroy$.next('');
  }
  constructor(
    private authService: AuthService,
    private graphService: GraphService,
    private dialogService: DialogService,
    private navigationService: NavigationService,
    private store: Store<AppState>,
  ) {
    this.navigationService.navExpanded = this.sideExpanded;
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectIsGenerativeAiEnabledAppSetting))
      .subscribe((aiEnabled) => (this.isGenerativeAiEnabled = aiEnabled.toLowerCase() == 'false' ? false : true));

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectTPBMVersionAppSetting))
      .subscribe((tpbmVersion) => (this.tpbmVersion = tpbmVersion));
  }

  async ngOnInit() {
    this.apiBuildVersion$ = this.navigationService.apiBuildVersionNumber$.asObservable();
    // make sure to wait until the service is configured before finding out if they are logged in
    this.authService.configured.subscribe((r) => {
      if (r === true) {
        this.loggedIn = this.authService.hasValidAzureADAccessToken();
        this.getCurrentUserDetails();
        this.getBuildUIBuildVersion();
      }
    });

    // for making sure that if a user isn't logged in we set the flag and get the details when received
    this.authService.tokenReceived.subscribe((r) => {
      this.loggedIn = r;
      this.getCurrentUserDetails();
    });

    const isFullScreen = localStorage.getItem(this.SIDEXPANDED_STORAGE_KEY);
    if (isFullScreen && isFullScreen === 'false') {
      this.sideExpanded = false;
      this.navigationService.navExpanded = this.sideExpanded;
    }
    this.navigationService.isGridExtended.subscribe(() => {
      this.toggleSideAfterGridExtend();
    });
  }

  public toggleSideAfterGridExtend() {
    const isFullScreen = localStorage.getItem(this.SIDEXPANDED_STORAGE_KEY);
    if (isFullScreen && isFullScreen === 'true') {
      this.sideExpanded = !this.navigationService.gridExpanded;
      this.navigationService.navExpanded = this.sideExpanded;
    }
  }

  public toggleSide() {
    this.sideExpanded = !this.sideExpanded;
    localStorage.setItem(this.SIDEXPANDED_STORAGE_KEY, String(this.sideExpanded));
    this.navigationService.navExpanded = this.sideExpanded;
  }

  public toggleTop() {
    this.topExpanded = !this.topExpanded;
    this.navigationService.navExpanded = this.sideExpanded;
  }

  public closeTop() {
    this.topExpanded = false;
  }

  public async logIn() {
    await this.authService.logIn();
    this.currentUser = await this.graphService.getCurrentUser();
    this.loggedIn = !this.loggedIn;
  }

  public async logOut() {
    const safetyCheckResult: DeactivationStatus = await this.authService.logoutSafetyCheck();
    if (safetyCheckResult.confirmationModalDisplayedToTheUser && safetyCheckResult.proceed) {
      return this.authService.logOut(LogoutReasonCode.userLoggedOut);
    }

    if (!safetyCheckResult.confirmationModalDisplayedToTheUser) {
      if (await this.dialogService.logoutConfirmation()) {
        return this.authService.logOut(LogoutReasonCode.userLoggedOut);
      }
    }

    return false;
  }

  public showNotificationsDialog(element) {
    if (element) {
      element.blur();
    } // prevent the dangling 'focus/active' highlighting
    this.dialogService.launchNotifications();
    return false;
  }

  private getCurrentUserDetails() {
    if (this.loggedIn) {
      this.graphService.getCurrentUser().then((u) => (this.currentUser = u));
    }
  }

  private getBuildUIBuildVersion() {
    this.navigationService.getJSON().subscribe((data) => {
      if (data != null) {
        this.uiBuildVersion = data.buildnumber.toString();
      }
    });
  }
}
